import React, { useState, useContext, useEffect } from 'react';
import {
	TextField,
	Button,
	Typography,
	MenuItem,
	InputLabel,
	Grid,
	FormControl,
	Select,
	InputAdornment,
	IconButton,
	Skeleton,
	Stack,
	Box
} from '@mui/material';
import { isEmpty } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faSave,
	faPenToSquare,
	faCodePullRequest,
	faGears,
	faCamera
} from '@fortawesome/free-solid-svg-icons';
import { ZoomIn } from '@mui/icons-material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

import { getToken } from '../../../api/Account';
import { DataContext } from '../../../context/DataContext';
import { simpleMessage } from '../../../helpers/Helpers';
import { tienePermiso } from '../../../helpers/permisos';
import {
	ConfirmDialog,
	EstadoChip,
	EstadoError,
	useAviso
} from '../../../components/base';

import {
	getCreditByIdAsync,
	remakeCreditAsync,
	updateCreditAsync
} from '../../../api/Credits';
import { getPeriodicidadListAsync } from '../../../api/Periodicidad';
import { getGestoresAsync } from '../../../api/Users';
import { getInteresListAsync } from '../../../api/InteresCorriente';
import { getPlazoByPeriodicidadAsync } from '../../../api/Plazo';
import SmallModal from '../../../components/modals/SmallModal';
import Reestructuracion from './Reestructuracion';
import CreditPhoto from './photoComponent/CreditPhoto';
import PreviewModal from '../../../components/modals/PreviewModal';

// Lo que devuelve `errorResponse` cuando el servidor dice que la sesión sigue abierta en otro equipo.
const ERROR_OTRA_SESION = 'Sesión activa en otro dispositivo';

// Respeta "reducir movimiento": el esqueleto queda quieto.
const sinAnimacion = {
	'@media (prefers-reduced-motion: reduce)': { animation: 'none' }
};

// Esqueleto con la forma del formulario mientras llegan los datos.
const CargandoCredito = () => (
	<Box role="status" aria-live="polite">
		<Box
			component="span"
			sx={{
				border: 0,
				clip: 'rect(0 0 0 0)',
				height: '1px',
				margin: '-1px',
				overflow: 'hidden',
				padding: 0,
				position: 'absolute',
				whiteSpace: 'nowrap',
				width: '1px'
			}}
		>
			Cargando el crédito…
		</Box>
		<Box aria-hidden="true">
			<Skeleton variant="text" width={180} sx={{ fontSize: '1.5rem', ...sinAnimacion }} />
			<Grid container spacing={2} sx={{ mt: 0 }}>
				{Array.from({ length: 8 }, (_, i) => (
					<Grid item xs={12} sm={6} key={i}>
						<Skeleton variant="rounded" height={56} sx={{ borderRadius: 4, ...sinAnimacion }} />
					</Grid>
				))}
			</Grid>
			<Skeleton variant="rounded" height={160} sx={{ mt: 3, ...sinAnimacion }} />
		</Box>
	</Box>
);

const CreditDetails = ({
	id,
	clientName,
	setClientName,
	isNominal,
	setIsNominal,
	setDataCredit,
	onPlanReconstruido
}) => {
	const { setIsLoading, setIsUnautorized, access } = useContext(DataContext);
	const aviso = useAviso();
	const token = getToken();

	const [selectedClient, setSelectedClient] = useState(null);

	const [periodicidadList, setPeriodicidadList] = useState([]);
	const [selectedPeriodicidad, setSelectedPeriodicidad] = useState('');

	const [plazoList, setPlazoList] = useState([]);
	const [selectedPlazo, setSelectedPlazo] = useState('');

	const [interesCorrienteList, setInteresCorrienteList] = useState([]);
	const [selectedInteresCorriente, setSelectedInteresCorriente] = useState('');

	const [gestorList, setGestorList] = useState([]);
	const [selectedGestor, setSelectedGestor] = useState('');
	const [montoPrincipal, setMontoPrincipal] = useState('');
	const [destino, setDestino] = useState('');

	const [fechaPrimeraCuota, setFechaPrimeraCuota] = useState(new Date());
	const [fechaEntrega, setFechaEntrega] = useState(new Date());

	const [isEdit, setIsEdit] = useState(false);
	const [showModal, setShowModal] = useState(false);

	const [creditPhotos, setCreditPhotos] = useState([]);
	const [editPhotos, setEditPhotos] = useState(false);
	const [localReload, setLocalReload] = useState(false);

	const [openModal, setOpenModal] = useState(false);
	const [selectedImage, setSelectedImage] = useState('');

	// Estado local de la pestaña: esqueleto mientras carga y error con "Reintentar".
	const [cargando, setCargando] = useState(true);
	const [error, setError] = useState(false);

	// Confirmación de Reconstruir (Reestructurar confirma al final de su propio formulario; se conserva `tipo` al cerrar
	// para que el texto no cambie durante la animación de salida).
	const [confirmacion, setConfirmacion] = useState({ abierto: false, tipo: null });
	const [procesando, setProcesando] = useState(false);

	const falloDeCarga = (mensaje) => {
		setIsLoading(false);
		setCargando(false);
		if (mensaje === 'Unauthorized') {
			setIsUnautorized(true);
			return;
		}
		setError(true);
		if (mensaje === ERROR_OTRA_SESION) {
			simpleMessage(mensaje, 'error');
		}
	};

	const cargar = async () => {
		setCargando(true);
		setError(false);
		setIsLoading(true);
		try {
			const result = await getCreditByIdAsync(token, id);

			if (!result.statusResponse) {
				falloDeCarga(result.error);
				return;
			}
			setClientName(result.data.result.client.fullName);
			setDataCredit(result.data.result);
			setSelectedClient(result.data.result.client);

			const resultPer = await getPeriodicidadListAsync(token);
			if (!resultPer.statusResponse) {
				falloDeCarga(resultPer.error);
				return;
			}
			setPeriodicidadList(resultPer.data.result);

			const resultGestores = await getGestoresAsync(token);
			if (!resultGestores.statusResponse) {
				falloDeCarga(resultGestores.error);
				return;
			}
			setGestorList(resultGestores.data.result);

			const resultIC = await getInteresListAsync(token);
			if (!resultIC.statusResponse) {
				falloDeCarga(resultIC.error);
				return;
			}
			setInteresCorrienteList(resultIC.data.result);

			const resultPlazo = await getPlazoByPeriodicidadAsync(
				token,
				result.data.result.periocidad.id
			);
			if (!resultPlazo.statusResponse) {
				falloDeCarga(resultPlazo.error);
				return;
			}

			setPlazoList(resultPlazo.data.result);
			setMontoPrincipal(result.data.result.montoPrincipal);
			setSelectedGestor(result.data.result.gestor);
			setDestino(result.data.result.destino);
			setSelectedPeriodicidad(result.data.result.periocidad.id);
			setSelectedPlazo(result.data.result.plazo.id);
			setSelectedInteresCorriente(result.data.result.tasaInteresCorriente.id);
			setFechaEntrega(new Date(result.data.result.fechaEntrega));
			setFechaPrimeraCuota(new Date(result.data.result.fechaPrimeraCuota));

			setCreditPhotos(result.data.result.creditPhotos);

			setIsLoading(false);
			setCargando(false);
		} catch (e) {
			// Sin red, `errorResponse` lanza una excepción: ni esqueleto eterno ni overlay encendido.
			setIsLoading(false);
			setCargando(false);
			setError('conexion');
		}
	};

	useEffect(() => {
		cargar();
	}, [localReload]);

	//Devuelve un entero positivo
	const funtionSetMontoSolicitado = (value) => {
		if (/^[0-9]+$/.test(value.toString()) || value === '') {
			setMontoPrincipal(value);
			return;
		}
	};

	const handleChangePeriodicidad = async (event) => {
		const selected = periodicidadList.filter((item) => item.id == event.target.value);
		let dias = selected[0].cuotaDias;

		var newDate = new Date();
		newDate.setDate(newDate.getDate() + dias);

		setFechaPrimeraCuota(newDate);
		setPlazoList([]);
		setSelectedPeriodicidad(event.target.value);

		if (event.target.value !== '') {
			setIsLoading(true);
			const result = await getPlazoByPeriodicidadAsync(token, event.target.value);
			if (!result.statusResponse) {
				setIsLoading(false);
				if (result.error === 'Unauthorized') {
					setIsUnautorized(true);
					return;
				}
				simpleMessage(result.error, 'error');
				return;
			}
			setPlazoList(result.data.result);
			setIsLoading(false);
		} else {
			setPlazoList([]);
		}
	};

	const saveChanges = async () => {
		if (validate()) {
			const datos = {
				id,
				montoPrincipal,
				fechaEntrega,
				fechaPrimeraCuota,
				destino,
				periocidadId: selectedPeriodicidad,
				plazoId: selectedPlazo,
				interesCorrienteId: selectedInteresCorriente,
				gestor: selectedGestor
			};

			setIsLoading(true);
			const result = await updateCreditAsync(token, datos);
			if (!result.statusResponse) {
				setIsLoading(false);

				if (result.error === 'Unauthorized') {
					setIsUnautorized(true);
					return;
				}

				simpleMessage(result.error, 'error');
				return;
			}

			setIsLoading(false);
			setIsEdit(false);
			simpleMessage('Crédito actualizado...!', 'success');
		}
	};

	//Validando campos ingresados
	const validate = () => {
		let isValid = true;
		if (isEmpty(selectedClient)) {
			simpleMessage('Debe seleccionar un cliente', 'error');
			return (isValid = false);
		}

		if (montoPrincipal === '') {
			simpleMessage('Debe ingresar un monto', 'error');
			return (isValid = false);
		}

		if (fechaPrimeraCuota === '') {
			simpleMessage('Debe ingresar fecha de primera cuota', 'error');
			return (isValid = false);
		}

		if (fechaPrimeraCuota === '') {
			simpleMessage('Debe ingresar una fecha de entrega', 'error');
			return (isValid = false);
		}

		if (selectedGestor === '') {
			simpleMessage('Debe seleccionar un gestor', 'error');
			return (isValid = false);
		}

		if (selectedPlazo === '') {
			simpleMessage('Debe seleccionar un plazo', 'error');
			return (isValid = false);
		}

		if (selectedInteresCorriente === '') {
			simpleMessage('Debe seleccionar una tasa de interes', 'error');
			return (isValid = false);
		}

		if (isEmpty(destino)) {
			simpleMessage('Debe seleccionar un destino de credito', 'error');
			return (isValid = false);
		}
		return isValid;
	};

	const remakeCredit = async () => {
		setIsLoading(true);
		try {
			const result = await remakeCreditAsync(token, id);
			if (!result.statusResponse) {
				setIsLoading(false);
				if (result.error === 'Unauthorized') {
					setIsUnautorized(true);
					return;
				}
				simpleMessage(result.error, 'error');
				return;
			}
			aviso.exito('Plan de pagos reconstruido');
			// El saldo y la mora del resumen ya no son los de antes: se descartan hasta cargar el plan.
			if (onPlanReconstruido) onPlanReconstruido();
			setIsLoading(false);
			setIsEdit(false);
		} catch (e) {
			setIsLoading(false);
			aviso.error('No se pudo conectar con el servidor. Inténtalo de nuevo.');
		}
	};

	const handleOpen = (imageUrl) => {
		setSelectedImage(imageUrl);
		setOpenModal(true);
	};

	const pedirConfirmacion = (tipo) => setConfirmacion({ abierto: true, tipo });
	const cerrarConfirmacion = () =>
		setConfirmacion((actual) => ({ ...actual, abierto: false }));

	// Solo aquí se llama a la función de verdad, y una sola vez por confirmación.
	// Reestructurar no pasa por aquí: abre el formulario, que pide su propia confirmación al final
	// (con las condiciones nuevas a la vista) antes de llamar a la API.
	const confirmar = async () => {
		setProcesando(true);
		try {
			await remakeCredit();
		} finally {
			setProcesando(false);
			cerrarConfirmacion();
		}
	};

	const puedeReconstruir = !isNominal && tienePermiso(access, 'CREDITS REGEN');
	// Cada acción pide el permiso que exige su endpoint (Controllers/Api/CreditsController.cs).
	const puedeEditar = tienePermiso(access, 'CREDITS UPDATE'); // UdateCredit
	const puedeCrear = tienePermiso(access, 'CREDITS CREATE'); // ReestructurarCredito y UpdateCreditPhotos (las fotos también piden UPDATE: se cambian en modo Editar)

	if (cargando) {
		return <CargandoCredito />;
	}

	if (error) {
		return (
			<EstadoError
				titulo={
					error === 'conexion'
						? 'No se pudo conectar con el servidor'
						: 'No se pudo cargar el crédito'
				}
				onReintentar={cargar}
			/>
		);
	}

	return (
		<div>
			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				alignItems={{ xs: 'flex-start', sm: 'center' }}
				justifyContent={'space-between'}
				spacing={1}
				sx={{ mb: 2 }}
			>
				<Stack direction="row" spacing={1.5} alignItems="center">
					<Typography
						component="h2"
						variant="h6"
						sx={{ fontWeight: 700, color: 'primary.main' }}
					>
						Datos del crédito
					</Typography>
					{isNominal ? <EstadoChip estado="info" label="Crédito nominal" /> : null}
				</Stack>

				{puedeEditar ? (
					<Button
						onClick={() => {
							setIsEdit(!isEdit);
						}}
						aria-pressed={isEdit}
						variant={isEdit ? 'contained' : 'outlined'}
						startIcon={<FontAwesomeIcon icon={faPenToSquare} />}
					>
						Editar
					</Button>
				) : null}
			</Stack>

			<Grid container spacing={2}>
				<Grid item xs={12} sm={6}>
					<Stack spacing={2}>
						<TextField
							disabled={!isEdit}
							required
							fullWidth
							onChange={(e) => funtionSetMontoSolicitado(e.target.value)}
							value={montoPrincipal}
							label={'Monto solicitado'}
							InputProps={{
								startAdornment: <InputAdornment position="start">C$</InputAdornment>
							}}
						/>

						<FormControl disabled={!isEdit} fullWidth sx={{ textAlign: 'left' }}>
							<InputLabel id="credito-gestor-label">Gestor</InputLabel>
							<Select
								labelId="credito-gestor-label"
								id="credito-gestor"
								onChange={(e) => setSelectedGestor(e.target.value)}
								value={selectedGestor}
								label="Gestor"
								sx={{ textAlign: 'left' }}
							>
								<MenuItem value="">
									<em>Seleccione un Gestor</em>
								</MenuItem>
								{gestorList.map((item) => {
									return (
										<MenuItem key={item.id} value={item.id}>
											{item.fullName}
										</MenuItem>
									);
								})}
							</Select>
						</FormControl>

						<TextField
							disabled={!isEdit}
							required
							fullWidth
							onChange={(e) => setDestino(e.target.value.toUpperCase())}
							value={destino}
							label={'Destino'}
						/>

						<DesktopDatePicker
							disabled={!isEdit}
							closeOnSelect
							label="Fecha de entrega"
							inputFormat="DD/MM/YYYY"
							value={fechaEntrega}
							onChange={(newValue) => setFechaEntrega(newValue)}
							renderInput={(params) => <TextField {...params} />}
						/>
					</Stack>
				</Grid>

				<Grid item xs={12} sm={6}>
					<Stack spacing={2}>
						<FormControl disabled={!isEdit} fullWidth sx={{ textAlign: 'left' }}>
							<InputLabel id="credito-periodicidad-label">Periodicidad</InputLabel>
							<Select
								labelId="credito-periodicidad-label"
								id="credito-periodicidad"
								onChange={handleChangePeriodicidad}
								value={selectedPeriodicidad}
								label="Periodicidad"
								sx={{ textAlign: 'left' }}
							>
								<MenuItem value="">
									<em>Seleccione un Periodo</em>
								</MenuItem>
								{periodicidadList.map((item) => {
									return (
										<MenuItem key={item.id} value={item.id}>
											{item.description}
										</MenuItem>
									);
								})}
							</Select>
						</FormControl>

						<FormControl disabled={!isEdit} fullWidth sx={{ textAlign: 'left' }}>
							<InputLabel id="credito-plazo-label">Plazo</InputLabel>
							<Select
								labelId="credito-plazo-label"
								id="credito-plazo"
								onChange={(e) => setSelectedPlazo(e.target.value)}
								value={selectedPlazo}
								label="Plazo"
								sx={{ textAlign: 'left' }}
							>
								<MenuItem value="">
									<em>Seleccione un Plazo</em>
								</MenuItem>
								{plazoList.map((item) => {
									return (
										<MenuItem key={item.id} value={item.id}>
											{item.description}
										</MenuItem>
									);
								})}
							</Select>
						</FormControl>

						<FormControl disabled={!isEdit} fullWidth sx={{ textAlign: 'left' }}>
							<InputLabel id="credito-tasa-label">Tasa de interés</InputLabel>
							<Select
								labelId="credito-tasa-label"
								id="credito-tasa"
								onChange={(e) => setSelectedInteresCorriente(e.target.value)}
								value={selectedInteresCorriente}
								label="Tasa de interés"
								sx={{ textAlign: 'left' }}
							>
								<MenuItem value="">
									<em>Seleccione una Tasa de Interes</em>
								</MenuItem>
								{interesCorrienteList.map((item) => {
									return (
										<MenuItem key={item.id} value={item.id}>
											{`${item.intCorriente.toFixed(2)} %`}
										</MenuItem>
									);
								})}
							</Select>
						</FormControl>

						<DesktopDatePicker
							disabled={!isEdit}
							closeOnSelect
							label="Fecha de primera cuota"
							inputFormat="DD/MM/YYYY"
							value={fechaPrimeraCuota}
							onChange={(newValue) => setFechaPrimeraCuota(newValue)}
							renderInput={(params) => <TextField {...params} />}
						/>
					</Stack>
				</Grid>

				<Grid item xs={12}>
					<Box
						sx={{
							border: 1,
							borderColor: 'divider',
							borderRadius: 3,
							p: { xs: 2, sm: 3 }
						}}
					>
						<Stack
							direction={{ xs: 'column', sm: 'row' }}
							alignItems={{ xs: 'flex-start', sm: 'center' }}
							justifyContent="space-between"
							spacing={1}
							sx={{ mb: 2 }}
						>
							<Typography
								component="h3"
								variant="subtitle1"
								sx={{ fontWeight: 700, color: 'primary.main' }}
							>
								Fotos del crédito
							</Typography>

							{puedeEditar && puedeCrear ? (
								<Button
									onClick={() => {
										setEditPhotos(true);
									}}
									variant="outlined"
									startIcon={<FontAwesomeIcon icon={faCamera} />}
									disabled={!isEdit}
								>
									Cambiar fotos
								</Button>
							) : null}
						</Stack>

						{creditPhotos.length === 0 ? (
							<Typography variant="body2" sx={{ color: 'text.secondary' }}>
								Este crédito no tiene fotos.
							</Typography>
						) : (
							<Grid container spacing={2}>
								{creditPhotos.map((preview, index) => {
									return (
										<Grid item xs={12} sm={6} key={index}>
											<Box
												sx={{
													position: 'relative',
													p: 1,
													height: 200,
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													border: 1,
													borderColor: 'divider',
													borderRadius: 2,
													bgcolor: 'background.default'
												}}
											>
												<img
													src={preview.imageFullPath}
													alt={`Foto del crédito ${index + 1}`}
													style={{
														maxWidth: '100%',
														maxHeight: '100%',
														objectFit: 'contain'
													}}
												/>

												{/* Botón de zoom */}
												<IconButton
													aria-label={`Ampliar la foto ${index + 1}`}
													sx={{
														position: 'absolute',
														top: 8,
														right: 8,
														bgcolor: 'background.paper',
														border: 1,
														borderColor: 'divider',
														'&:hover': { bgcolor: 'background.paper' },
														width: 32,
														height: 32
													}}
													onClick={() => handleOpen(preview.imageFullPath)}
												>
													<ZoomIn sx={{ fontSize: 18 }} />
												</IconButton>
											</Box>
										</Grid>
									);
								})}
							</Grid>
						)}
					</Box>
				</Grid>
			</Grid>

			{puedeEditar ? (
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
					<Button
						variant="contained"
						startIcon={<FontAwesomeIcon icon={faSave} />}
						onClick={() => saveChanges()}
						size="large"
						disabled={!isEdit}
					>
						Actualizar crédito
					</Button>
				</Box>
			) : (
				<></>
			)}

			{/* Acciones que cambian el plan de pagos: separadas del formulario, cada una con su consecuencia a la vista. */}
			{puedeReconstruir || puedeCrear ? (
				<Box
					component="section"
					aria-labelledby="credito-acciones-plan"
					sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}
				>
					<Typography
						id="credito-acciones-plan"
						component="h3"
						variant="subtitle1"
						sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}
					>
						Cambios en el plan de pagos
					</Typography>

					<Stack spacing={2} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
						{puedeReconstruir ? (
							<Stack
								direction={{ xs: 'column', sm: 'row' }}
								justifyContent="space-between"
								alignItems={{ xs: 'flex-start', sm: 'center' }}
								spacing={1.5}
							>
								<Box>
									<Typography sx={{ fontWeight: 700 }}>Reconstruir plan de pagos</Typography>
									<Typography variant="body2" sx={{ color: 'text.secondary' }}>
										Vuelve a generar el plan de pagos de este crédito.
									</Typography>
								</Box>
								<Button
									onClick={() => pedirConfirmacion('reconstruir')}
									variant="outlined"
									color="warning"
									startIcon={<FontAwesomeIcon icon={faGears} />}
								>
									Reconstruir
								</Button>
							</Stack>
						) : null}

						{puedeCrear ? (
							<Stack
								direction={{ xs: 'column', sm: 'row' }}
								justifyContent="space-between"
								alignItems={{ xs: 'flex-start', sm: 'center' }}
								spacing={1.5}
							>
								<Box>
									<Typography sx={{ fontWeight: 700 }}>Reestructurar crédito</Typography>
									<Typography variant="body2" sx={{ color: 'text.secondary' }}>
										Cancela este crédito y crea uno nuevo con otras condiciones.
									</Typography>
								</Box>
								<Button
									onClick={() => setShowModal(true)}
									variant="outlined"
									color="error"
									startIcon={<FontAwesomeIcon icon={faCodePullRequest} />}
								>
									Reestructurar
								</Button>
							</Stack>
						) : null}
					</Stack>
				</Box>
			) : null}

			<ConfirmDialog
				abierto={confirmacion.abierto}
				cargando={procesando}
				titulo="Reconstruir plan de pagos"
				mensaje={
					<>
						<Typography component="p" sx={{ mb: 1 }}>
							{`Reconstruir vuelve a generar el plan de pagos del crédito #${id}${clientName ? ` de ${clientName}` : ''}.`}
						</Typography>
						<Typography component="p" sx={{ color: 'text.secondary' }}>
							Revisa el plan de pagos cuando termine.
						</Typography>
					</>
				}
				textoConfirmar="Reconstruir"
				onConfirmar={confirmar}
				onCancelar={cerrarConfirmacion}
			/>

			<SmallModal
				titulo={'Reestructurar crédito'}
				isVisible={showModal}
				setVisible={setShowModal}
			>
				<Reestructuracion
					setShowModal={setShowModal}
					id={id}
					clientName={clientName}
					periodicidadList={periodicidadList}
				/>
			</SmallModal>

			<CreditPhoto
				id={id}
				abierto={editPhotos}
				setEditPhotos={setEditPhotos}
				onActualizado={() => setLocalReload(!localReload)}
			/>

			<PreviewModal
				openModal={openModal}
				selectedImage={selectedImage}
				setOpenModal={setOpenModal}
				setSelectedImage={setSelectedImage}
			/>
		</div>
	);
};

export default CreditDetails;
